/**
 * What the patient sees and does on the plan the clinic sent (Treatment Plan
 * spec v1.4: S-04 the plan page, S-05 Accept, S-06 Not right now), drawn from
 * canvas boards 03 and 04.
 *
 * Everything here reads the plan through the API's patient view, which carries
 * no cost, margin or OPR figure (PG-006). There is no card entry anywhere: the
 * clinic charges in its own billing system and MCOS never charges the patient.
 * There is never a total for a length of time (TL-005).
 *
 * "I have a question" opens the guided questions (phase 4, S-07). The done
 * screen's "Questions any time?" line opens Message Care Team (S-10, board 4).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { portalDownload, portalRequest, usePortal } from '../../lib/portal';
import { Card, TONE, formatPortalDate, usePortalQuery } from './shell';
import { ArrowLeft } from './icons';
import { PlanAnswers, QuestionSheet } from './PlanQuestions';

export interface PatientPlanItem {
  therapyId: string;
  name: string;
  category: string;
  purpose: string;
  dose: string;
  howToTake: string;
  sideEffects: string;
  price: string;
  unit: 'month' | 'visit' | 'bottle';
}

export interface PatientPlanPayload {
  plan: {
    planId: string;
    version: number;
    status: 'sent' | 'viewed' | 'question_open' | 'accepted' | 'prescribed' | 'not_now';
    providerNote: string;
    /** WT-05, WT-06: "Your plan from Dr. Lin" and "Prescribed by Dr. Sarah Lin, DO", once signed. */
    title?: string;
    prescribedBy?: string;
    signedAt?: string | null;
    items: PatientPlanItem[];
    monthlyTotal: string;
    perVisitNote: string | null;
    whatToExpect: { headline: string; checkpoints: string[]; clinicianNote: string; disclaimer: string | null };
  };
  clinicName: string | null;
  clinicianName: string | null;
  goalHeadline: string | null;
  nextLabDraw: string | null;
  twinStats: { similarPatients: number; attainmentPercent: number; attainmentLabel: string } | null;
  acceptedOn: string | null;
  canAct: boolean;
  addressOnFile: { line1: string; line2: string; city: string; state: string; zip: string } | null;
}

export const UNIT_TEXT = { month: '/mo', visit: '/visit', bottle: '/bottle' } as const;

export function usePatientPlanPayload() {
  return usePortalQuery<PatientPlanPayload>('plan-current', '/plans/current');
}

/** Log that the patient opened the plan once, which also marks it viewed (EV-02). */
export function useLogPlanOpened(payload: PatientPlanPayload | undefined) {
  const queryClient = useQueryClient();
  const planId = payload?.plan.planId;
  const status = payload?.plan.status;
  useEffect(() => {
    if (!planId || status !== 'sent') return;
    void portalRequest(`/plans/${planId}/events`, { method: 'POST', body: { type: 'plan_opened' } })
      .then(() => queryClient.invalidateQueries({ queryKey: ['portal', 'plan-current'] }))
      .catch(() => undefined);
  }, [planId, status, queryClient]);
}

/** EV-03: the sections the spec names. */
export type PlanSection = 'items' | 'price' | 'what_to_expect' | 'twin';

/**
 * EV-03: log a section once it has been on screen for 3 seconds, once per page
 * view. Returns the ref to put on the section.
 */
export function useSectionViewed<T extends HTMLElement>(planId: string | undefined, section: PlanSection) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!planId || !node || typeof IntersectionObserver === 'undefined') return;
    let timer: number | undefined;
    let shownAt = 0;
    let logged = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (logged) return;
        if (entry?.isIntersecting) {
          shownAt = Date.now();
          timer = window.setTimeout(() => {
            logged = true;
            observer.disconnect();
            void portalRequest(`/plans/${planId}/events`, {
              method: 'POST',
              body: { type: 'section_viewed', detail: { section, seconds: Math.round((Date.now() - shownAt) / 1000) } },
            }).catch(() => undefined);
          }, 3000);
        } else {
          window.clearTimeout(timer);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [planId, section]);
  return ref;
}

/* ------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------ */

const CREAM_LINE = '#e6e3dc';
const FOREST_SOFT = '#e6f0ea';
const FOREST_INK = '#1b3f35';
const MUTED = '#5b6168';

function Label({ children }: { children: ReactNode }) {
  return (
    <div className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: '#1c2b3a' }}>
      {children}
    </div>
  );
}

function Tick({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.4 4.4L19 7.2" />
    </svg>
  );
}

const buttonPrimary =
  'flex w-full items-center justify-center rounded-[12px] bg-forest text-[16px] font-semibold text-white transition-colors hover:bg-forest-deep disabled:opacity-50';
const field = 'h-[44px] w-full rounded-[10px] border bg-white px-[12px] text-[14px] outline-none focus:border-forest';

/** "Recovery & Performance IV Kit" -> "Recovery & Performance IV", for "+ ... visits". */
export function visitName(name: string): string {
  return name.replace(/\s+Kit$/i, '');
}

/* ------------------------------------------------------------------ *
 * S-04: the buttons, or the result of the decision already made
 * ------------------------------------------------------------------ */

export function PlanActions({ payload }: { payload: PatientPlanPayload }) {
  const [open, setOpen] = useState<'accept' | 'not_now' | 'question' | 'reaccept' | null>(null);
  const clinic = payload.clinicName ?? 'Your clinic';
  const status = payload.plan.status;
  const active = status === 'accepted' || status === 'prescribed';
  // CN-006: a material change to the consent asks a patient on an active plan to accept it again.
  const reaccept = usePortalQuery<{ needed: boolean; consentVersion: string | null }>(`reaccept-${payload.plan.planId}`, `/plans/${payload.plan.planId}/reaccept`, active);

  return (
    <>
      {active && reaccept.data?.needed && (
        <Card className="px-[14px] py-[12px]" style={{ borderColor: '#ecd9a0', background: '#fdf6e3' }} data-testid="reaccept-needed">
          <p className="text-[14px] font-semibold" style={{ color: TONE.heading }}>{clinic} updated its consent</p>
          <p className="mt-[3px] text-[13px] leading-snug" style={{ color: MUTED }}>
            Please read the updated consent and accept it to keep your plan.
          </p>
          <button type="button" className="mt-2 h-[44px] w-full rounded-[10px] bg-forest text-[14.5px] font-semibold text-white" onClick={() => setOpen('reaccept')}>
            Read and accept
          </button>
        </Card>
      )}
      {open === 'reaccept' && <ReacceptFlow payload={payload} onClose={() => setOpen(null)} />}
      {active && (
        <Card className="px-[14px] py-[12px]" data-testid="plan-accepted">
          <p className="text-[14px] font-semibold" style={{ color: TONE.heading }}>
            {status === 'prescribed' ? 'Your prescription has been sent to the pharmacy' : `Accepted ${formatPortalDate(payload.acceptedOn)}`}
          </p>
          <p className="mt-[3px] text-[13px] leading-snug" style={{ color: MUTED }}>
            {status === 'prescribed' ? `${clinic} will let you know when it ships.` : `${clinic} is placing your prescription.`}
          </p>
          {/* S-27: the patient's own copies, as signed. */}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px]" data-testid="plan-documents">
            <span style={{ color: MUTED }}>Your documents:</span>
            <button type="button" className="font-semibold underline" style={{ color: 'var(--forest)' }} onClick={() => void portalDownload(`/plans/${payload.plan.planId}/record.pdf?kind=plan`, 'my-treatment-plan.pdf')}>
              Treatment plan
            </button>
            <button type="button" className="font-semibold underline" style={{ color: 'var(--forest)' }} onClick={() => void portalDownload(`/plans/${payload.plan.planId}/record.pdf?kind=consent`, 'my-consent.pdf')}>
              Consent
            </button>
          </div>
        </Card>
      )}
      {status === 'not_now' && (
        <Card className="px-[14px] py-[12px]" style={{ background: 'var(--paper-deep)' }} data-testid="plan-not-now">
          <p className="text-[14px] font-semibold" style={{ color: TONE.heading }}>
            Your plan stays here
          </p>
          <p className="mt-[3px] text-[13px] leading-snug" style={{ color: MUTED }}>
            You can accept any time.
          </p>
        </Card>
      )}
      {status === 'question_open' && (
        <Card className="px-[14px] py-[12px]" data-testid="plan-question-open">
          <p className="text-[14px] font-semibold" style={{ color: TONE.heading }}>
            We're answering your question
          </p>
          <p className="mt-[3px] text-[13px] leading-snug" style={{ color: MUTED }}>
            {clinic} will reply here. You can still accept the plan any time.
          </p>
        </Card>
      )}
      <PlanAnswers planId={payload.plan.planId} />
      {payload.canAct && (
        <div className="space-y-[10px]" data-testid="plan-actions">
          <button type="button" className={`${buttonPrimary} h-[50px]`} onClick={() => setOpen('accept')}>
            Accept plan
          </button>
          <div className="grid grid-cols-2 gap-[10px]">
            <button
              type="button"
              className="flex h-[46px] items-center justify-center rounded-[12px] border text-[15px] font-semibold transition-colors hover:bg-[#eeede8]"
              style={{ borderColor: '#1c2b3a', color: '#1c2b3a' }}
              onClick={() => setOpen('question')}
            >
              I have a question
            </button>
            <button
              type="button"
              className="flex h-[46px] items-center justify-center rounded-[12px] text-[15px] transition-colors hover:bg-[#eeede8]"
              style={{ color: '#1c2b3a' }}
              onClick={() => setOpen('not_now')}
            >
              Not right now
            </button>
          </div>
        </div>
      )}
      {open === 'accept' && <AcceptFlow payload={payload} onClose={() => setOpen(null)} />}
      {open === 'not_now' && <NotNowSheet payload={payload} onClose={() => setOpen(null)} />}
      {open === 'question' && <QuestionSheet planId={payload.plan.planId} clinicName={clinic} onClose={() => setOpen(null)} />}
    </>
  );
}

interface Reason {
  code: string;
  label: string;
}

function useRefreshAfter() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['portal'] });
}

/* ------------------------------------------------------------------ *
 * S-05: Accept. One confirm screen and one done screen (board 04).
 * ------------------------------------------------------------------ */

const ZIP = /^\d{5}(-\d{4})?$/;

/** What the server builds for this plan's Accept screen (S-24). */
interface ConsentScreen {
  consentVersion: string | null;
  consentApproved: boolean;
  testText: boolean;
  clinicianShort: string;
  points: Array<{ id: string; text: string; planSpecific: boolean }>;
  tickPlan: string;
  tickCharge: string;
  location: { state: string | null; stateName: string };
  hrt: { required: boolean; formText: string | null; signed: boolean; formApproved: boolean };
  fullConsent: { version: string | null; text: string };
}

function TickBox({ checked, onChange, children, testId }: { checked: boolean; onChange: (value: boolean) => void; children: ReactNode; testId?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-[10px] text-[13px] leading-normal" data-testid={testId}>
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span
        aria-hidden="true"
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[5px] border text-white peer-focus-visible:ring-2 peer-focus-visible:ring-forest"
        style={checked ? { background: 'var(--forest)', borderColor: 'var(--forest)' } : { background: '#fff', borderColor: '#b9b4a8', color: 'transparent' }}
      >
        <Tick size={14} />
      </span>
      <span>{children}</span>
    </label>
  );
}

interface PlanDocuments {
  plan: { version: number; signedBy: string | null; signedOn: string | null };
  consent: { acceptedAt: string; version: string } | null;
  hrtForm: { signedAt: string | null; version: string | null } | null;
}

/** "$110 a month" in the charge tick is bold, as board 16 draws it. */
function boldAmount(text: string): ReactNode {
  const match = text.match(/\$[\d,]+(?:\.\d{2})? a month/);
  if (!match || match.index === undefined) return text;
  return (
    <>
      {text.slice(0, match.index)}
      <b>{match[0]}</b>
      {text.slice(match.index + match[0].length)}
    </>
  );
}

const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';

function AcceptFlow({ payload, onClose }: { payload: PatientPlanPayload; onClose: () => void }) {
  const refresh = useRefreshAfter();
  const { me } = usePortal();
  const prefill = payload.addressOnFile;
  const [ship, setShip] = useState({
    line1: prefill?.line1 ?? '',
    line2: prefill?.line2 ?? '',
    city: prefill?.city ?? '',
    state: prefill?.state ?? '',
    zip: prefill?.zip ?? '',
  });
  const [changed, setChanged] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [chargeAgreed, setChargeAgreed] = useState(false);
  const [hrtAgreed, setHrtAgreed] = useState(false);
  const [locationState, setLocationState] = useState('');
  const [showFull, setShowFull] = useState(false);
  const [done, setDone] = useState(false);
  const consent = usePortalQuery<ConsentScreen>(`plan-consent-${payload.plan.planId}`, `/plans/${payload.plan.planId}/consent`);
  // Board 16: where they are today and where it ships, as one line; "Change" opens the fields. With nothing on file they start open.
  const [editingPlace, setEditingPlace] = useState(!prefill);

  // Where the patient is today starts as the state on file; they can change it (SC-02).
  useEffect(() => {
    if (consent.data?.location.state && !locationState) setLocationState(consent.data.location.state);
  }, [consent.data?.location.state, locationState]);

  useEffect(() => {
    void portalRequest(`/plans/${payload.plan.planId}/events`, { method: 'POST', body: { type: 'accept_started' } }).catch(() => undefined);
  }, [payload.plan.planId]);

  const accept = useMutation({
    mutationFn: () =>
      portalRequest(`/plans/${payload.plan.planId}/accept`, {
        method: 'POST',
        body: {
          shipTo: ship,
          healthCheck: { changed: changed === true, note: changed ? note : undefined },
          agreed,
          chargeAgreed,
          locationState: locationState.trim().toUpperCase(),
          consentVersion: consent.data?.consentVersion ?? '',
        },
      }),
    onSuccess: () => setDone(true),
  });

  const signHrt = useMutation({
    mutationFn: () => portalRequest(`/plans/${payload.plan.planId}/hrt-form`, { method: 'POST', body: { agreed: hrtAgreed } }),
    onSuccess: () => void consent.refetch(),
  });

  const close = () => {
    onClose();
    if (done) void refresh();
  };

  const clinic = payload.clinicName ?? 'Your clinic';
  const firstName = me?.patient.firstName ?? '';
  const monthly = payload.plan.items.filter((item) => item.unit !== 'visit');
  const visits = payload.plan.items.filter((item) => item.unit === 'visit');
  const ready =
    ship.line1.trim() &&
    ship.city.trim() &&
    ship.state.trim().length === 2 &&
    ZIP.test(ship.zip.trim()) &&
    changed !== null &&
    (!changed || note.trim()) &&
    /^[A-Za-z]{2}$/.test(locationState.trim()) &&
    consent.data?.consentApproved &&
    (!consent.data.hrt.required || consent.data.hrt.signed) &&
    agreed &&
    chargeAgreed;

  if (done) return <AcceptDone planId={payload.plan.planId} clinic={clinic} firstName={firstName} onClose={close} />;

  const stateName = consent.data?.location.state === locationState.trim().toUpperCase() ? consent.data.location.stateName : locationState.trim().toUpperCase();
  const shipLine = [ship.line1, ship.line2].filter((part) => part.trim()).join(', ') + (ship.city ? `, ${ship.city}, ${ship.state}` : '');

  return (
    <FullScreen label="Confirm your plan">
      <div className="flex shrink-0 items-center gap-[10px] border-b bg-white px-5 pb-3 pt-[max(16px,env(safe-area-inset-top))]" style={{ borderColor: CREAM_LINE }}>
        <button type="button" onClick={onClose} aria-label="Back to the plan" className="flex" style={{ color: 'var(--forest)' }}>
          <ArrowLeft size={22} />
        </button>
        <span className="text-[17px] font-semibold">Almost done</span>
      </div>
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-[18px] pb-4 pt-1">
        <div className="mt-3 rounded-[14px] border bg-white p-[14px]" style={{ borderColor: CREAM_LINE }}>
          <div className="flex justify-between">
            <Label>Your plan</Label>
            <button type="button" onClick={onClose} className="text-[13px] font-semibold underline" style={{ color: 'var(--forest)' }}>
              Edit
            </button>
          </div>
          <div className="mt-[6px] text-[15px] font-semibold">{monthly.map((item) => item.name).join(', ')}</div>
          {visits.length > 0 && (
            <div className="text-[13px]" style={{ color: MUTED }}>
              + {visits.map((item) => visitName(item.name)).join(', ')} visits
            </div>
          )}
          <div className="mt-[6px] text-[15px]">
            <b>{payload.plan.monthlyTotal}</b>
            {payload.plan.perVisitNote && <span style={{ color: MUTED }}> {payload.plan.perVisitNote}</span>}
          </div>
        </div>

        <div className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: CREAM_LINE }}>
          <Label>Quick health check</Label>
          <div className="mt-[6px] text-[14px] leading-normal">
            Anything changed since your labs? New medications, allergies, pregnancy or a new diagnosis.
          </div>
          <div className="mt-[10px] flex gap-2" role="radiogroup" aria-label="Anything changed since your labs">
            {[
              { value: false, text: 'No changes' },
              { value: true, text: 'Yes, something changed' },
            ].map((option) => {
              const on = changed === option.value;
              return (
                <button
                  key={option.text}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setChanged(option.value)}
                  className={`flex h-11 flex-1 items-center justify-center rounded-[10px] text-[14px] ${on ? 'border-2 font-semibold' : 'border'}`}
                  style={on ? { borderColor: 'var(--forest)', background: FOREST_SOFT, color: FOREST_INK } : { borderColor: '#d8d3c8' }}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
          {changed && (
            <input aria-label="What changed" className={`${field} mt-2`} style={{ borderColor: '#d8d3c8' }} placeholder="What changed" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
          <div className="mt-2 text-[12px]" style={{ color: MUTED }}>
            If yes, your clinician reviews it before anything is sent.
          </div>
        </div>

        <div className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: CREAM_LINE }} data-testid="consent-points">
          <Label>Before you accept</Label>
          {consent.isLoading && <p className="mt-2 text-[13px]" style={{ color: MUTED }}>Loading</p>}
          {consent.data && !consent.data.consentApproved && (
            <p className="mt-2 text-[13px] leading-normal" role="status" data-testid="consent-waiting">
              The consent text is waiting for approval, so this plan cannot be accepted yet. {clinic} has been told.
            </p>
          )}
          {consent.data?.consentApproved && (
            <>
              {consent.data.testText && (
                <p className="mt-2 rounded-[8px] px-[10px] py-[6px] text-[12px]" style={{ background: '#fff4e5', color: '#7a4b12' }} data-testid="consent-test-text">
                  Test wording for this demo. Real patients see the text approved by counsel.
                </p>
              )}
              <ul className="mt-2 flex flex-col gap-[9px]">
                {consent.data.points.map((point) => (
                  <li key={point.id} className="flex gap-[10px] text-[13.5px] leading-[1.45]">
                    <span className="mt-[2px] shrink-0" style={{ color: 'var(--forest)' }}>
                      <Tick size={14} />
                    </span>
                    <span>
                      {point.text}
                      {point.planSpecific && (
                        <span className="ml-1.5 inline-block rounded-[4px] px-[5px] py-[1px] align-middle text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ background: '#eeede8', color: MUTED }}>
                          This plan
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <button type="button" className="mt-[10px] text-[13.5px] font-semibold underline" style={{ color: 'var(--forest)' }} onClick={() => setShowFull((value) => !value)} aria-expanded={showFull}>
                {showFull ? 'Hide the full consent' : 'Read the full consent'}
              </button>
              {showFull && (
                <div className="mt-2 max-h-[220px] overflow-y-auto whitespace-pre-line rounded-[8px] p-[10px] text-[12px] leading-normal" style={{ background: '#f7f6f2' }} data-testid="consent-full">
                  {consent.data.fullConsent.text}
                </div>
              )}
            </>
          )}
        </div>

        {consent.data?.hrt.required && !consent.data.hrt.signed && (
          <div className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: CREAM_LINE }} data-testid="hrt-form">
            <Label>Sign your hormone therapy form</Label>
            {consent.data.hrt.formApproved ? (
              <>
                <div className="mt-2 max-h-[180px] overflow-y-auto whitespace-pre-line text-[12px] leading-normal">{consent.data.hrt.formText}</div>
                <div className="mt-2">
                  <TickBox checked={hrtAgreed} onChange={setHrtAgreed}>I have read the hormone therapy form and I agree to it.</TickBox>
                </div>
                <button type="button" className={`${buttonPrimary} mt-2 h-[44px]`} disabled={!hrtAgreed || signHrt.isPending} onClick={() => signHrt.mutate()}>
                  Sign the form
                </button>
                {signHrt.error && (
                  <p className="mt-1 text-[13px] text-alert" role="alert">
                    {signHrt.error instanceof Error ? signHrt.error.message : 'Could not sign the form'}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 text-[13px]">The hormone therapy form is waiting for approval, so this plan cannot be accepted yet.</p>
            )}
          </div>
        )}

        <div className="rounded-[14px] border bg-white p-[14px]" style={{ borderColor: CREAM_LINE }} data-testid="place-block">
          <div className="flex items-start justify-between gap-2">
            <Label>{stateName ? `You are in ${stateName} today` : 'Where are you today?'}</Label>
            {!editingPlace && (
              <button type="button" onClick={() => setEditingPlace(true)} className="text-[13px] font-semibold underline" style={{ color: 'var(--forest)' }}>
                Change
              </button>
            )}
          </div>
          {editingPlace ? (
            <div className="mt-2 grid grid-cols-6 gap-[6px]">
              <label className="col-span-6 flex items-center justify-between gap-2 text-[13px]" style={{ color: MUTED }}>
                Your clinician can only treat you in a state where they are licensed.
                <input
                  aria-label="State you are in today"
                  className="h-[44px] w-[64px] shrink-0 rounded-[10px] border bg-white px-[12px] text-center text-[14px] uppercase outline-none focus:border-forest"
                  style={{ borderColor: '#d8d3c8' }}
                  placeholder="ST"
                  maxLength={2}
                  value={locationState}
                  onChange={(e) => setLocationState(e.target.value)}
                />
              </label>
              <input aria-label="Address line 1" className={`${field} col-span-6`} style={{ borderColor: '#d8d3c8' }} placeholder="Ship to: street address" value={ship.line1} onChange={(e) => setShip({ ...ship, line1: e.target.value })} />
              <input aria-label="Address line 2" className={`${field} col-span-6`} style={{ borderColor: '#d8d3c8' }} placeholder="Apartment or suite, optional" value={ship.line2} onChange={(e) => setShip({ ...ship, line2: e.target.value })} />
              <input aria-label="City" className={`${field} col-span-3`} style={{ borderColor: '#d8d3c8' }} placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
              <input aria-label="State" className={`${field} col-span-1`} style={{ borderColor: '#d8d3c8' }} placeholder="ST" maxLength={2} value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} />
              <input aria-label="ZIP code" className={`${field} col-span-2`} style={{ borderColor: '#d8d3c8' }} placeholder="ZIP" inputMode="numeric" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} />
            </div>
          ) : (
            <div className="mt-[6px] text-[14px] leading-normal" data-testid="ship-to-saved">
              Ship to {shipLine}
              {ship.zip ? ` ${ship.zip}` : ''}
            </div>
          )}
        </div>

        {consent.data?.consentApproved && (
          <div className="flex flex-col gap-[12px] px-[2px]">
            <TickBox checked={agreed} onChange={setAgreed} testId="tick-plan">
              {consent.data.tickPlan}
            </TickBox>
            <TickBox checked={chargeAgreed} onChange={setChargeAgreed} testId="tick-charge">
              {boldAmount(consent.data.tickCharge)}
            </TickBox>
          </div>
        )}

        {accept.error && (
          <p className="text-[13px] text-alert" role="alert">
            {accept.error instanceof Error ? accept.error.message : 'Could not accept the plan'}
          </p>
        )}
      </div>
      <div className="shrink-0 border-t bg-white px-[18px] pb-[calc(18px+env(safe-area-inset-bottom))] pt-3" style={{ borderColor: CREAM_LINE }}>
        <button type="button" className={`${buttonPrimary} h-[52px]`} disabled={!ready || accept.isPending} onClick={() => accept.mutate()}>
          Accept plan
        </button>
      </div>
    </FullScreen>
  );
}

/** Board 16, phone 3: done, and the plan and consent to keep. */
function AcceptDone({ planId, clinic, firstName, onClose }: { planId: string; clinic: string; firstName: string; onClose: () => void }) {
  const docs = usePortalQuery<PlanDocuments>(`plan-documents-${planId}`, `/plans/${planId}/documents`);
  const d = docs.data;
  const row = (title: string, sub: string, kind: 'plan' | 'consent' | 'hrt', file: string) => (
    <li className="flex items-center gap-3 py-[10px]">
      <span className="shrink-0" style={{ color: MUTED }} aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5M9 13h6M9 17h6" />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-semibold" style={{ color: '#1c2b3a' }}>{title}</span>
        <span className="block text-[12.5px]" style={{ color: MUTED }}>{sub}</span>
      </span>
      <button type="button" className="text-[13.5px] font-semibold underline" style={{ color: '#1c2b3a' }} onClick={() => void portalDownload(`/plans/${planId}/record.pdf?kind=${kind}`, file)}>
        Download
      </button>
    </li>
  );
  return (
    <FullScreen label="Plan accepted">
      <div className="flex-1 overflow-y-auto px-[18px] pb-4 pt-1">
        <div className="flex flex-col gap-3">
          <div className="h-[30px]" />
          <div className="grid h-16 w-16 place-items-center rounded-full" style={{ background: FOREST_SOFT, color: 'var(--forest)' }}>
            <Tick size={30} />
          </div>
          <h1 className="font-display text-[30px] font-semibold leading-[1.15]" style={{ color: '#1c2b3a' }}>
            You're all set, {firstName}.
          </h1>
          <p className="text-[15px] leading-normal" style={{ color: MUTED }}>
            {clinic} will place your prescription. Your monthly check-in arrives by text.
          </p>
          <div className="rounded-[14px] border bg-white px-4 pb-1 pt-3" style={{ borderColor: CREAM_LINE }} data-testid="accept-done">
            <Label>Your documents</Label>
            <ul className="divide-y" style={{ borderColor: CREAM_LINE }}>
              {d
                ? [
                    row(`Treatment plan v${d.plan.version}`, d.plan.signedBy ? `Signed by ${d.plan.signedBy}, ${shortDate(d.plan.signedOn)}` : 'Your plan', 'plan', 'my-treatment-plan.pdf'),
                    d.consent ? row('Consent you accepted', new Date(d.consent.acceptedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }), 'consent', 'my-consent.pdf') : null,
                    d.hrtForm?.signedAt ? row('Hormone therapy form', `Signed ${shortDate(d.hrtForm.signedAt)}`, 'hrt', 'my-hormone-therapy-form.pdf') : null,
                  ]
                : <li className="py-3 text-[13px]" style={{ color: MUTED }}>Loading</li>}
            </ul>
          </div>
          <p className="text-[12.5px]" style={{ color: MUTED }}>
            You can see and download these any time in your portal.
          </p>
          <Link
            to="/portal/messages/new"
            className="block rounded-[12px] px-4 py-3 text-[14px] leading-snug underline-offset-2 hover:underline"
            style={{ background: FOREST_SOFT, color: '#1c2b3a' }}
            data-testid="accept-done-message"
          >
            Questions any time? Message your care team from your plan.
          </Link>
        </div>
      </div>
      <div className="shrink-0 px-[18px] pb-[calc(18px+env(safe-area-inset-bottom))] pt-3">
        <button
          type="button"
          className="h-[50px] w-full rounded-[12px] border bg-white text-[16px] font-semibold"
          style={{ borderColor: 'var(--forest)', color: 'var(--forest)' }}
          onClick={onClose}
        >
          Back to my plan
        </button>
      </div>
    </FullScreen>
  );
}

/** CN-006: the updated consent, read and accepted again with both ticks, as at Accept. */
function ReacceptFlow({ payload, onClose }: { payload: PatientPlanPayload; onClose: () => void }) {
  const refresh = useRefreshAfter();
  const consent = usePortalQuery<ConsentScreen>(`plan-consent-${payload.plan.planId}`, `/plans/${payload.plan.planId}/consent`);
  const [agreed, setAgreed] = useState(false);
  const [chargeAgreed, setChargeAgreed] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      portalRequest(`/plans/${payload.plan.planId}/reaccept`, {
        method: 'POST',
        body: { agreed, chargeAgreed, consentVersion: consent.data?.consentVersion ?? '' },
      }),
    onSuccess: () => {
      void refresh();
      onClose();
    },
  });
  const c = consent.data;
  return (
    <FullScreen label="Updated consent">
      <div className="flex shrink-0 items-center gap-[10px] border-b bg-white px-5 pb-3 pt-[max(16px,env(safe-area-inset-top))]" style={{ borderColor: CREAM_LINE }}>
        <button type="button" onClick={onClose} aria-label="Back" className="flex" style={{ color: 'var(--forest)' }}><ArrowLeft size={22} /></button>
        <span className="text-[17px] font-semibold" style={{ color: TONE.heading }}>Updated consent</span>
      </div>
      <div className="flex-1 overflow-y-auto px-[18px] pb-4 pt-3" data-testid="reaccept-screen">
        <p className="text-[14px] leading-normal" style={{ color: MUTED }}>Your plan stays the same. These are the key points of the updated consent.</p>
        {consent.isLoading && <p className="mt-3 text-[13px]" style={{ color: MUTED }}>Loading</p>}
        <ul className="mt-3 flex flex-col gap-2">
          {c?.points.map((p) => (
            <li key={p.id} className="flex gap-2 rounded-[12px] border bg-white px-3 py-2.5 text-[13.5px] leading-snug" style={{ borderColor: CREAM_LINE, color: TONE.heading }}>
              <span style={{ color: 'var(--forest)' }}><Tick size={14} /></span>
              {p.text}
            </li>
          ))}
        </ul>
        {c?.fullConsent.text && (
          <details className="mt-3 text-[13px]" style={{ color: MUTED }}>
            <summary className="cursor-pointer font-semibold" style={{ color: 'var(--forest)' }}>Read the full consent</summary>
            <p className="mt-2 whitespace-pre-line">{c.fullConsent.text}</p>
          </details>
        )}
      </div>
      <div className="shrink-0 border-t bg-white px-[18px] pb-[calc(18px+env(safe-area-inset-bottom))] pt-3" style={{ borderColor: CREAM_LINE }}>
        {c && (
          <div className="flex flex-col gap-2.5">
            <TickBox checked={agreed} onChange={setAgreed} testId="reaccept-tick-plan">{c.tickPlan}</TickBox>
            <TickBox checked={chargeAgreed} onChange={setChargeAgreed} testId="reaccept-tick-charge">{boldAmount(c.tickCharge)}</TickBox>
          </div>
        )}
        {send.error && <p className="mt-2 text-[13px] text-alert" role="alert">{send.error instanceof Error ? send.error.message : 'Could not save'}</p>}
        <button type="button" disabled={!agreed || !chargeAgreed || send.isPending || !c} onClick={() => send.mutate()} className="mt-3 h-[50px] w-full rounded-[12px] bg-forest text-[16px] font-semibold text-white disabled:opacity-50">
          Accept the updated consent
        </button>
      </div>
    </FullScreen>
  );
}

/** The accept and done screens fill the phone, over the tab bar, as board 04 draws them. */
function FullScreen({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-[#f7f6f2]" role="dialog" aria-modal="true" aria-label={label}>
      <div className="flex h-full w-full max-w-[430px] flex-col">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * S-06: Not right now, a bottom sheet (board 04)
 * ------------------------------------------------------------------ */

function NotNowSheet({ payload, onClose }: { payload: PatientPlanPayload; onClose: () => void }) {
  const refresh = useRefreshAfter();
  const reasons = usePortalQuery<{ reasons: Reason[] }>('not-now-reasons', '/not-now-reasons');
  const [code, setCode] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [checkBackOn, setCheckBackOn] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = useMutation({
    mutationFn: () =>
      portalRequest(`/plans/${payload.plan.planId}/not-now`, {
        method: 'POST',
        body: { reasonCode: code, comment: comment || null, checkBackOn: code === 'timing' ? checkBackOn : null },
      }),
    onSuccess: () => {
      void refresh();
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Not right now">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0" style={{ background: 'rgba(28,43,58,0.45)' }} />
      <div
        className="relative mx-auto flex max-h-[92vh] w-full max-w-[430px] flex-col gap-[10px] overflow-y-auto rounded-t-[22px] bg-white px-[18px] pb-[calc(18px+env(safe-area-inset-bottom))] pt-5"
        data-testid="not-now-sheet"
      >
        <div className="h-1 w-10 shrink-0 self-center rounded-sm" style={{ background: '#d8d3c8' }} />
        <h2 className="font-display text-[22px] font-semibold" style={{ color: '#1c2b3a' }}>
          No problem. What's holding you back?
        </h2>
        <p className="text-[13px]" style={{ color: MUTED }}>
          Pick one. It helps your clinic help you.
        </p>
        <div className="flex flex-col gap-[10px]" role="radiogroup" aria-label="Reason">
          {(reasons.data?.reasons ?? []).map((reason) => {
            const on = code === reason.code;
            return (
              <button
                key={reason.code}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setCode(reason.code)}
                className={`flex min-h-11 items-center gap-3 rounded-[10px] px-3 text-left text-[14px] ${on ? 'border-2' : 'border'}`}
                style={on ? { borderColor: 'var(--forest)', background: FOREST_SOFT } : { borderColor: CREAM_LINE }}
              >
                <span
                  className="box-border h-[18px] w-[18px] shrink-0 rounded-full"
                  style={on ? { border: '5px solid var(--forest)' } : { border: '2px solid #b9b4a8' }}
                />
                {reason.label}
              </button>
            );
          })}
        </div>
        {code === 'timing' && (
          <label className="block text-[13px]" style={{ color: MUTED }}>
            Check back on
            <input type="date" className={`${field} mt-1`} style={{ borderColor: '#d8d3c8' }} value={checkBackOn} onChange={(e) => setCheckBackOn(e.target.value)} />
          </label>
        )}
        <textarea
          aria-label="Anything to add? (optional)"
          className="h-[60px] w-full resize-none rounded-[10px] border px-3 py-[10px] text-[13px] outline-none focus:border-forest"
          style={{ borderColor: '#d8d3c8' }}
          maxLength={1000}
          placeholder="Anything to add? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        {send.error && (
          <p className="text-[13px] text-alert" role="alert">
            {send.error instanceof Error ? send.error.message : 'Could not save your answer'}
          </p>
        )}
        <button
          type="button"
          className={`${buttonPrimary} h-[50px]`}
          disabled={!code || (code === 'timing' && !checkBackOn) || send.isPending}
          onClick={() => send.mutate()}
        >
          Send
        </button>
        <p className="text-center text-[12px]" style={{ color: MUTED }}>
          Your plan stays here. You can accept any time.
        </p>
      </div>
    </div>
  );
}
